import { useRef } from "react";
import { useIntl } from "react-intl";
import { WmChartBar } from "@watermarkinsights/ripple-react";
import type { ChartConfig } from "@watermarkinsights/ripple";
import { AiDataCaptureCaption } from "../AiDataCaptureCaption";
import { DownloadActionMenu } from "./DownloadActionMenu";
import { useExportToPng } from "../export-conversation/useExportToPng";
import { buildBarChartCsvData, downloadTableAsCsv } from "../ai-chat-message-utils";
import type { BarChartPayload } from "../ai-chat-message-utils";
import { CHART_BAR_CONFIG_ATTRIBUTE, CHART_BAR_DATA_ATTRIBUTE } from "./chartBarSerialization";

const GRID_MAX = "auto";
const DEFAULT_VALUE_FORMAT = "amount";
const GROUPED_LEGEND_POSITION = "bottom";
const SIMPLE_LEGEND_POSITION = "none";
const GROUPED_COLOR_MODE = "discrete";

interface AiBarChartProps {
  payload: BarChartPayload;
  readOnly?: boolean;
}

export const AiBarChart = ({ payload, readOnly = false }: AiBarChartProps) => {
  const intl = useIntl();
  const { label, categoryAxisLabel, valueAxisLabel, valueFormat, sort, data } = payload;
  const elementRef = useRef<HTMLDivElement>(null);
  const { isExporting, handleExport } = useExportToPng({
    elementRef,
    fileTitle: label || "Currents_Conversation_Chart",
    errorMessage: intl.formatMessage({ id: "app.toolboxChat.action.downloadPngError" }),
  });

  const isGrouped = data.some((item) => Boolean(item.groupName));

  const chartConfig: ChartConfig = {
    label: label ?? "Bar Chart",
    showLabel: false,
    categoryAxisLabel,
    valueAxisLabel: valueAxisLabel ?? "",
    printMode: false,
    printModeFormat: "amount",
    gridMax: GRID_MAX,
    sort: sort ?? "none",
    valueFormat: valueFormat ?? DEFAULT_VALUE_FORMAT,
    showGrid: true,
    showLegend: isGrouped ? GROUPED_LEGEND_POSITION : SIMPLE_LEGEND_POSITION,
    ...(isGrouped && { colors: GROUPED_COLOR_MODE }),
  };

  const chartData = data.map((item) => ({
    barLegend: item.barLegend,
    amount: item.amount,
    popoverTitle:
      item.popoverTitle ??
      (item.groupName ? `${item.barLegend} -- ${item.groupName}` : item.barLegend),
    popoverText: item.popoverText,
    ...(item.groupName && { groupName: item.groupName }),
  }));

  const handleDownloadCsv = () => {
    const { columns, rows } = buildBarChartCsvData(data, categoryAxisLabel, valueAxisLabel);
    downloadTableAsCsv(columns, rows, label);
  };

  return (
    <div ref={elementRef}>
      <div className="tcw-chart-card">
        <div className="tcw-chart-header">
          {label && <p className="tcw-chart-label">{label}</p>}
          {!readOnly && (
            <DownloadActionMenu
              tooltip={intl.formatMessage({ id: "app.toolboxChat.action.downloadChart" })}
              onDownloadPng={handleExport}
              onDownloadCsv={handleDownloadCsv}
              disabled={isExporting}
            />
          )}
        </div>
        <div className="tcw-chart-wrapper">
          <WmChartBar
            config={chartConfig}
            data={chartData}
            {...{
              [CHART_BAR_CONFIG_ATTRIBUTE]: JSON.stringify(chartConfig),
              [CHART_BAR_DATA_ATTRIBUTE]: JSON.stringify(chartData),
            }}
          />
        </div>
        <AiDataCaptureCaption />
      </div>
    </div>
  );
};
